import {
  createBrowserRouter,
} from "react-router-dom";
import { lazy } from "react";
import ProtectedRoute from "./Components/Shared/ProtectedRoute/ProtectedRoute";
import RouteErrorFallback from "./Components/Shared/RouteErrorFallback/RouteErrorFallback";
import { basename } from "./Constants/env"

const Login = lazy(() => import("./Components/Login"));
const Home = lazy(() => import(/* webpackPrefetch: true */ "./Components/Home"));
const ForgotPassword = lazy(() => import("./Components/ForgotPassword/ForgotPassword"));
const Register = lazy(() => import("./Components/Register/Register"));
const ResetPassword = lazy(() => import("./Components/ResetPassword/ResetPassword"));
const Billing = lazy(() => import(/* webpackPrefetch: true */ "./Components/Billing"));
const Agents = lazy(() => import(/* webpackPrefetch: true */ "./Components/Agents"));
const AgentDetail = lazy(() => import(/* webpackPrefetch: true */ "./Components/Agents/AgentDetail"));
const KnowledgeDatabase = lazy(() => import(/* webpackPrefetch: true */ "./Components/KnowledgeDatabase/KnowledgeDatabasePage"));
const KnowledgeDatabaseEntries = lazy(() => import(/* webpackPrefetch: true */ "./Components/KnowledgeDatabase/KnowledgeDatabaseDetail/KnowledgeDatabaseDetail"));
const UserProfile = lazy(() => import(/* webpackPrefetch: true */ "./Components/Profile/Profile"));
const MessageDisplay = lazy(() => import(/* webpackPrefetch: true */ "./Components/Messages"));
const ChangePassword = lazy(() => import(/* webpackPrefetch: true */ "./Components/Profile/ChangePassword/ChangePassword"));
const UserList = lazy(() => import(/* webpackPrefetch: true */ "./Components/Users/Users"));
const Transactions = lazy(() => import(/* webpackPrefetch: true */ "./Components/Transactions"));
const Credentials = lazy(() => import(/* webpackPrefetch: true */ "./Components/Credentials"));
const Channels = lazy(() => import(/* webpackPrefetch: true */ "./Components/Channels"));
const ChannelDetail = lazy(() => import(/* webpackPrefetch: true */ "./Components/Channels/ChannelDetail"));
const EmailVerificationPage = lazy(() => import("./Components/EmailVerification/EmailVerification"));
const PaymentStatus = lazy(() => import(/* webpackPrefetch: true */ "./Components/PaymentStatus/PaymentStatus"));
const Settings = lazy(() => import(/* webpackPrefetch: true */ "./Components/Settings/Settings"));
const Tickets = lazy(() => import(/* webpackPrefetch: true */ "./Components/Tickets/Tickets"));
const AcceptTerms = lazy(() => import("./Components/AcceptTerms/AcceptTerms"));
const NotFound = lazy(() => import("./Components/NotFound/NotFound"));
const DataExportImportPage = lazy(() => import(/* webpackPrefetch: true */ "./Components/DataExportImport/DataExportImportPage"));

// Wrapper to provide error handling within protected route layout
const ProtectedRouteWithError = ({ component, ...rest }) => (
  <ProtectedRoute component={component} {...rest} />
);

const Router = createBrowserRouter([
  {
    path: "/tickets",
    element: <ProtectedRoute component={Tickets} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/tickets/:id",
    element: <ProtectedRoute component={Tickets} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/",
    element: <ProtectedRoute component={Home} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/users",
    element: <ProtectedRoute component={UserList} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/billing",
    element: <ProtectedRoute component={Billing} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/transactions",
    element: <ProtectedRoute component={Transactions} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/credentials",
    element: <ProtectedRoute component={Credentials} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/channels",
    element: <ProtectedRoute component={Channels} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/channels/:channelId",
    element: <ProtectedRoute component={ChannelDetail} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/agents",
    element: <ProtectedRoute component={Agents} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/agents/:agentId",
    element: <ProtectedRoute component={AgentDetail} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/messages",
    element: <ProtectedRoute component={MessageDisplay} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/knowledge-base",
    element: <ProtectedRoute component={KnowledgeDatabase} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/knowledge-base/:id",
    element: <ProtectedRoute component={KnowledgeDatabaseEntries} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/profile",
    element: <ProtectedRoute component={UserProfile} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/change-password",
    element: <ProtectedRoute component={ChangePassword} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/settings",
    element: <ProtectedRoute component={Settings} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/payments",
    element: <ProtectedRoute component={PaymentStatus} useLayout={false} />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "/accept-terms",
    Component: AcceptTerms,
  },
  {
    path: "/404",
    Component: NotFound,
  },
  {
    path: "login",
    Component: Login,
  },
  {
    path: "register",
    Component: Register,
  },
  {
    path: "forgot-password",
    Component: ForgotPassword,
  },
  {
    path: "reset-password/:token",
    Component: ResetPassword,
  },
  {
    path: "verify-email/:token",
    Component: EmailVerificationPage,
  },
  {
    path: "/data-exports",
    element: <ProtectedRoute component={DataExportImportPage} exact />,
    errorElement: <ProtectedRouteWithError component={RouteErrorFallback} />,
  },
  {
    path: "*",
    Component: NotFound,
  },
],
  { basename }
);

export default Router;
